import logger from '@ayocore/platform/common/helpers/logger';
import { ControllerStore } from '@ayocore/platform/client/controllers/ControllerStore';
import {
  LlmStreamController,
  runLlmStream,
  answerPendingToolCall,
  stopLlmStream,
} from './LlmStreamController';
import { getAssistantMessageHistory, getBaseSessionId } from '../rpc';

/** Сообщение в истории чата ассистента (UI-модель) */
export interface AssistantChatMessage {
  /** Стабильный ключ рендера: `db_<id>` | `local_<n>` | `stream_<n>` */
  id: string;
  /** Числовой id в БД; null — оптимистичное/стримящееся сообщение до реконсиляции */
  dbId: number | null;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

export interface AssistantChatState {
  messages: AssistantChatMessage[];
  /** Первая загрузка истории */
  isLoadingHistory: boolean;
  /** Идёт подгрузка более ранних сообщений */
  loadingOlder: boolean;
  /** Есть ли более ранние сообщения (кнопка «загрузить ещё») */
  hasMoreOlder: boolean;
  /** Идёт стрим ответа ассистента (зеркало LlmStreamController.isLoading) */
  isStreaming: boolean;
  error: string | null;
}

const INITIAL_STATE: AssistantChatState = {
  messages: [],
  isLoadingHistory: false,
  loadingOlder: false,
  hasMoreOlder: false,
  isStreaming: false,
  error: null,
};

/** Размер страницы истории */
const PAGE_SIZE = 30;

/**
 * Контроллер чата с BASE_ASSISTANT.
 *
 * Ответственность:
 * - история сообщений из БД (первая страница + пагинация «назад»);
 * - отправка сообщения (обычный ход / ответ на HITL-паузу) с оптимистичным добавлением;
 * - зеркалирование стрим-контента из LlmStreamController в последнее сообщение;
 * - реконсиляция после завершения стрима: оптимистичные сообщения заменяются
 *   персистентными из БД (id из БД — канон, локальные «stream_/local_» — временные);
 * - устаревшие ответы отбрасываются generation-счётчиком.
 *
 * Статусы tool calls и pendingUserInput НЕ зеркалируются — UI читает их напрямую
 * из LlmStreamController (один источник истины, меньше синхронизации).
 */
export class AssistantChatController extends ControllerStore<AssistantChatState> {
  private static instance: AssistantChatController | null = null;

  /** Счётчик операций загрузки — устаревший ответ не должен перезаписать состояние */
  private loadGeneration = 0;

  /** Счётчик локальных id для оптимистичных сообщений */
  private localCounter = 0;

  /** id текущего стримящегося сообщения ассистента (в терминах UI-ключей) */
  private streamingMessageId: string | null = null;

  /** Был ли активен стрим на прошлом тике синхронизации (для отлова завершения) */
  private wasStreaming = false;

  private unsubscribeLlm: (() => void) | null = null;

  /** Гард однократной инициализации (панель может монтироваться многократно) */
  private initStarted = false;

  constructor() {
    super(INITIAL_STATE);
    this.unsubscribeLlm = LlmStreamController.getInstance().subscribe(() => {
      this.syncStreamState();
    });
  }

  /** Синглтон для dock-панели (единственный чат BASE_ASSISTANT на пользователя) */
  static getInstance(): AssistantChatController {
    if (!AssistantChatController.instance) {
      AssistantChatController.instance = new AssistantChatController();
    }
    return AssistantChatController.instance;
  }

  /**
   * Инициализация при первом открытии панели: WS-подписка + первая страница истории.
   * Идемпотентна — повторные вызовы (ремоунт панели) ничего не делают.
   */
  async init(): Promise<void> {
    if (this.initStarted) return;
    this.initStarted = true;
    await this.ensureWebSocketSubscription();
    await this.loadChat();
  }

  /** Загрузить первую страницу истории (сбрасывает текущее состояние сообщений) */
  async loadChat(): Promise<void> {
    const generation = ++this.loadGeneration;
    this.setState({ isLoadingHistory: true, error: null });

    try {
      const result = await getAssistantMessageHistory({ limit: PAGE_SIZE });
      if (generation !== this.loadGeneration) return;

      if (!result.success) {
        this.setState({ isLoadingHistory: false, error: result.error ?? 'Ошибка загрузки истории' });
        return;
      }

      this.setState({
        messages: (result.messages ?? []).map(msg => this.mapDbMessage(msg)),
        hasMoreOlder: result.hasMore ?? false,
        isLoadingHistory: false,
        error: null,
      });
    } catch (error) {
      if (generation !== this.loadGeneration) return;
      logger.error('❌ [AssistantChat] loadChat error:', error);
      this.setState({
        isLoadingHistory: false,
        error: error instanceof Error ? error.message : 'Ошибка загрузки истории',
      });
    }
  }

  /** Подгрузить более ранние сообщения (кнопка «загрузить ещё») */
  async loadOlderMessages(): Promise<void> {
    const state = this.getState();
    if (state.loadingOlder || !state.hasMoreOlder) return;

    const oldest = state.messages.find(msg => msg.dbId != null);
    if (!oldest?.dbId) return;

    const generation = this.loadGeneration;
    this.setState({ loadingOlder: true });

    try {
      const result = await getAssistantMessageHistory({
        limit: PAGE_SIZE,
        beforeId: oldest.dbId,
      });
      if (generation !== this.loadGeneration) return;

      if (!result.success) {
        this.setState({ loadingOlder: false, error: result.error ?? 'Ошибка загрузки' });
        return;
      }

      const older = (result.messages ?? []).map(msg => this.mapDbMessage(msg));
      this.setState({
        messages: [...older, ...this.getState().messages],
        hasMoreOlder: result.hasMore ?? false,
        loadingOlder: false,
      });
    } catch (error) {
      if (generation !== this.loadGeneration) return;
      logger.error('❌ [AssistantChat] loadOlderMessages error:', error);
      this.setState({ loadingOlder: false });
    }
  }

  /**
   * Отправить сообщение: обычный ход либо ответ на HITL-паузу
   * (решается по pendingUserInput в LlmStreamController).
   */
  async sendMessage(prompt: string): Promise<void> {
    const trimmed = prompt.trim();
    if (!trimmed) return;

    const llmCtrl = LlmStreamController.getInstance();
    const pendingUserInput = llmCtrl.getSnapshot().pendingUserInput;

    const userMessage: AssistantChatMessage = {
      id: `local_${++this.localCounter}`,
      dbId: null,
      role: 'user',
      content: trimmed,
      timestamp: new Date(),
    };
    this.setState({ messages: [...this.getState().messages, userMessage], error: null });

    try {
      if (pendingUserInput) {
        await answerPendingToolCall(pendingUserInput.call_id, trimmed);
      } else {
        await runLlmStream(trimmed, { maxIterations: 10 });
      }
    } catch (error) {
      logger.error('❌ [AssistantChat] sendMessage error:', error);
      this.setState({ error: error instanceof Error ? error.message : 'Ошибка отправки' });
    }
  }

  /** Прервать текущий ход ассистента (кнопка «стоп») */
  async stopStreaming(): Promise<void> {
    await stopLlmStream();
  }

  /** Закрыть плашку ошибки */
  clearError(): void {
    this.setState({ error: null });
    LlmStreamController.getInstance().setError(null);
  }

  /** Освобождение ресурсов (тесты/hot-reload; в проде синглтон живёт всегда) */
  dispose(): void {
    if (this.unsubscribeLlm) {
      this.unsubscribeLlm();
      this.unsubscribeLlm = null;
    }
    super.dispose();
  }

  // ── private ──────────────────────────────────────────────────────────────

  /**
   * Зеркалирование состояния стрима в историю:
   * дельты контента обновляют/создают стримящееся сообщение ассистента;
   * завершение стрима запускает реконсиляцию с БД.
   */
  private syncStreamState(): void {
    const llmState = LlmStreamController.getInstance().getSnapshot();
    const state = this.getState();
    const isStreaming = llmState.isLoading;

    if (isStreaming) {
      // Взводим флаг по факту старта стрима, а не по появлению контента:
      // иначе завершение БЕЗ единого чанка (мгновенная ошибка провайдера,
      // пауза до первого текста) не финализируется и ошибка теряется молча.
      this.wasStreaming = true;

      if (llmState.content) {
        const messages = state.messages;
        const last = messages[messages.length - 1];

        if (last && last.id === this.streamingMessageId) {
          this.batch(() => {
            this.setState({
              messages: [...messages.slice(0, -1), { ...last, content: llmState.content }],
              isStreaming: true,
            });
          });
        } else {
          const streamingId = `stream_${++this.localCounter}`;
          this.streamingMessageId = streamingId;
          this.setState({
            messages: [
              ...messages,
              {
                id: streamingId,
                dbId: null,
                role: 'assistant',
                content: llmState.content,
                timestamp: new Date(),
              },
            ],
            isStreaming: true,
          });
        }
      } else if (!state.isStreaming) {
        this.setState({ isStreaming: true });
      }
      return;
    }

    if (this.wasStreaming) {
      this.wasStreaming = false;
      this.streamingMessageId = null;
      this.batch(() => {
        this.setState({ isStreaming: false });
        if (llmState.error) {
          this.setState({ error: llmState.error });
        }
      });
      // Канонизируем хвост из БД (user-сообщение и ответ ассистента уже сохранены сервером)
      void this.reconcileTail();
      return;
    }

    if (isStreaming !== state.isStreaming) {
      this.setState({ isStreaming });
    }
  }

  /**
   * Реконсиляция хвоста истории после завершения стрима: свежая страница из БД
   * заменяет оптимистичные (`local_/stream_`) сообщения и пересекающийся диапазон
   * персистентных. Более ранние подгруженные страницы не трогаются.
   */
  private async reconcileTail(): Promise<void> {
    const generation = this.loadGeneration;
    try {
      const result = await getAssistantMessageHistory({ limit: PAGE_SIZE });
      if (generation !== this.loadGeneration) return;
      if (!result.success || !result.messages) return;

      const fresh = result.messages.map(msg => this.mapDbMessage(msg));
      const state = this.getState();

      if (fresh.length === 0) {
        // В БД пусто — убираем только локальные временные сообщения
        this.setState({ messages: state.messages.filter(msg => msg.dbId != null) });
        return;
      }

      const minFreshId = fresh[0].dbId ?? 0;
      const kept = state.messages.filter(msg => msg.dbId != null && msg.dbId < minFreshId);
      this.setState({
        messages: [...kept, ...fresh],
        // hasMoreOlder обновляем только если старых страниц не осталось
        ...(kept.length === 0 ? { hasMoreOlder: result.hasMore ?? false } : {}),
      });
    } catch (error) {
      logger.warn('⚠️ [AssistantChat] reconcileTail failed (история догонит при следующей загрузке):', error);
    }
  }

  /** Получить sessionId (RPC при необходимости) и убедиться, что WS-подписка активна */
  private async ensureWebSocketSubscription(): Promise<void> {
    const llmCtrl = LlmStreamController.getInstance();
    let sessionId = llmCtrl.getSnapshot().sessionId;

    if (!sessionId) {
      try {
        const result = await getBaseSessionId();
        if (result.success && result.sessionId) {
          sessionId = result.sessionId;
          llmCtrl.setSession(sessionId);
        } else {
          logger.error('❌ [AssistantChat] Failed to get session ID:', result.error);
          return;
        }
      } catch (error) {
        logger.error('❌ [AssistantChat] Error getting session ID:', error);
        return;
      }
    }

    llmCtrl.ensureSubscription(sessionId);
  }

  private mapDbMessage(msg: {
    id: number;
    role: 'user' | 'assistant';
    content: string;
    timestamp: string;
  }): AssistantChatMessage {
    return {
      id: `db_${msg.id}`,
      dbId: msg.id,
      role: msg.role,
      content: msg.content,
      timestamp: new Date(msg.timestamp),
    };
  }
}
